import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react'

import type { FileAuthPreviewFolder } from '@core/app/api/filePreviewApi'
import type {
    ApplyV2FileAttachment,
    ApplyV2TempBase64File,
    ApplyV2TempCourseFile,
    V2CourseFile,
} from '@core/app/types/applyV2Type'
import type { GetAttachment } from '@core/app/types/profileApiType'

type FilePreview =
    // | File
    ApplyV2TempCourseFile | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File | GetAttachment

export type FilePreviewData = FilePreview & {
    CandidateFileID?: number
    Ext?: string
    downloadUrl?: string
    FileTypeID?: number
    File?: string
    type?: 'url' | 'document' | 'corporationFile'
    ext?: string
    downloadLink?: string
}

interface FilePreviewContextType {
    previewFile: FilePreviewData | null
    isOpen: boolean
    openPreview: (file: FilePreviewData, folder: FileAuthPreviewFolder, disabled?: boolean) => void
    closePreview: () => void
    getFileName: () => string
    fileExtension: string
    folder: FileAuthPreviewFolder | null
    disabled?: boolean
}

const FilePreviewContext = createContext<FilePreviewContextType | undefined>(undefined)

export const useFilePreview = (): FilePreviewContextType => {
    const context = useContext(FilePreviewContext)
    if (!context) {
        throw new Error('useFilePreview must be used within FilePreviewProvider')
    }
    return context
}

interface FilePreviewProviderProps {
    children: ReactNode
}

export const FilePreviewProvider: React.FC<FilePreviewProviderProps> = ({ children }) => {
    const [previewFile, setPreviewFile] = useState<FilePreviewData | null>(null)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [currentFolder, setCurrentFolder] = useState<FileAuthPreviewFolder | null>(null)
    const [isDisabled, setIsDisabled] = useState<boolean>(false)
    const [downloadUrlLink, setDownloadUrlLink] = useState<string | null>(null)

    const openPreview = useCallback(
        (file: FilePreviewData, folder: FileAuthPreviewFolder, disabled?: boolean, downloadUrl?: string) => {
            setPreviewFile(file)
            setCurrentFolder(folder)
            setIsOpen(true)
            setIsDisabled(!!disabled)
            setDownloadUrlLink(downloadUrl ?? file.downloadUrl ?? file.downloadLink ?? null)
        },
        []
    )

    const closePreview = useCallback(() => {
        setIsOpen(false)
        setTimeout(() => {
            setPreviewFile(null)
            setCurrentFolder(null)
            setDownloadUrlLink(null)
        }, 300)
    }, [])

    const getFileName = useCallback((): string => {
        if (previewFile instanceof File) {
            return previewFile.name
        }

        // ApplyV2TempCourseFile
        if (previewFile && 'name' in previewFile && typeof previewFile.name === 'string') {
            return previewFile.name
        }

        // GetAttachment
        if (previewFile && 'Filename' in previewFile && typeof previewFile.Filename === 'string') {
            return previewFile.Filename
        }

        // V2CourseFile
        if (previewFile && 'fileName' in previewFile && typeof previewFile.fileName === 'string') {
            return previewFile.fileName
        }

        // ApplyV2TempBase64File
        if (
            previewFile &&
            'base64' in previewFile &&
            'fileName' in previewFile &&
            typeof previewFile.fileName === 'string'
        ) {
            return previewFile.fileName
        }

        return 'Unknown file'
    }, [previewFile])

    const fileExtension = useMemo(() => {
        if (previewFile && 'Ext' in previewFile) {
            return previewFile.Ext || ''
        } else {
            return getFileName()?.split('.').pop() || ''
        }
    }, [getFileName, previewFile])

    return (
        <FilePreviewContext.Provider
            value={{
                previewFile: previewFile ? ({ ...previewFile, downloadUrl: downloadUrlLink } as FilePreviewData) : null,
                isOpen,
                openPreview,
                closePreview,
                getFileName,
                fileExtension,
                folder: currentFolder as FileAuthPreviewFolder,
                disabled: isDisabled,
            }}
        >
            {children}
        </FilePreviewContext.Provider>
    )
}
